<template>
    <div>
        <b-datatable
            ref="grid"
            :key="datatableKey"
            v-model="selectedItems"
            :columns="columns"
            :endpoint="listRoute"
            :hidden-columns="hiddenColumns"
            :initial-filters="initialFilters"
            class="patients-data-table"
            item-key="id"
            :grid-title="gridTitle"
            :has-bulk-operations="true"
            has-filter-selector
            :keep-filters-prefix="keepFiltersPrefix"
        >
            <template #buttons>
                <div class="d-flex">
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on: onTooltip, attrs: attrsTooltip }">
                                <v-menu offset-y>
                                    <template #activator="{ on, attrs }">
                                        <v-btn
                                            color="primary"
                                            dark
                                            icon
                                            v-bind="{ ...attrs, ...attrsTooltip }"
                                            v-on="{ ...on, ...onTooltip }"
                                        >
                                            <v-icon>mdi-file-export-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click="exportRecord('csv')">
                                            <v-list-item-icon>
                                                <v-icon>mdi mdi-file-delimited</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title>{{
                                                t('patients::button.export_csv')
                                            }}</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="exportRecord('xls')">
                                            <v-list-item-icon>
                                                <v-icon>mdi mdi-file-excel</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title>{{
                                                t('patients::button.export_xls')
                                            }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </template>
                            <span>{{ t('patients::button.export') }}</span>
                        </v-tooltip>
                    </div>

                    <div class="px-2">
                        <upload-patient-from-pdf-button></upload-patient-from-pdf-button>
                    </div>
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    v-if="can('api.v1.patients.store') && can('patients.create')"
                                    color="primary"
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="createItemUrl(hasPharmacySite ? initialFilters.pharmacy_site_id : false)"
                                >
                                    <v-icon>mdi-plus-circle</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('patients::button.create') }}</span>
                        </v-tooltip>
                    </div>
                </div>
            </template>

            <template #filters="{ filters }">
                <v-col cols="3" class="pb-0">
                    <b-filter-input v-model="filters.filters.name" :label="t('users::label.name')"></b-filter-input>
                </v-col>

                <v-col cols="3" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.dms_id"
                        :label="t('patients::grid.label.dms_id')"
                    ></b-filter-input>
                </v-col>

                <v-col cols="3" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.nhs_id"
                        :label="t('patients::grid.label.nhs_id')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="3" class="pb-0">
                    <b-filter-input
                        v-model="filters.filters.dob"
                        :label="t('patients::grid.label.date_of_birth')"
                    ></b-filter-input>
                </v-col>
                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.health_status"
                        :items="filtersOptions.health_status"
                        :label="t('patients::grid.label.health_status')"
                        :clearable="filters.filters.health_status !== null"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.max_dispensing"
                        :items="filtersOptions.max_dispensing"
                        :label="t('patients::grid.label.max_dispensing')"
                        :clearable="filters.filters.max_dispensing !== null"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.validity"
                        :items="filtersOptions.validity"
                        :label="t('patients::grid.label.validity')"
                        :clearable="filters.filters.validity !== null"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.is_registered"
                        :items="filtersOptions.is_registered"
                        :label="t('patients::grid.label.is_registered')"
                        :clearable="filters.filters.is_registered !== null"
                    >
                    </b-filter-select>
                </v-col>

                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.status"
                        :items="filtersOptions.status"
                        :label="t('patients::grid.label.status')"
                        :clearable="filters.filters.status !== null"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.prohibited"
                        :items="filtersOptions.prohibited"
                        :label="t('patients::grid.label.prohibited')"
                        :clearable="filters.filters.prohibited !== null"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.has_treatment_break"
                        :items="filtersOptions.treatmentBreakOptions"
                        :label="t('patients::grid.label.treatment_break')"
                        :clearable="filters.filters.has_treatment_break !== null"
                    >
                    </b-filter-select>
                </v-col>
                <v-col cols="3" class="pb-0">
                    <b-filter-select
                        v-model="filters.filters.dispensed"
                        :label="t('patients::form.label.dispensed_against_latest_blood_result')"
                        :items="filtersOptions.dispensed"
                        :clearable="filters.filters.dispensed !== null"
                    ></b-filter-select>
                </v-col>
            </template>

            <template #bulkOperations>
                <v-btn
                    v-if="can('patients.generate-labels')"
                    color="primary"
                    rounded
                    outlined
                    small
                    @click="print_labels()"
                >
                    {{ t('patients::button.print_labels') }}
                </v-btn>
            </template>

            <template #[`item.name`]="{ item }">
                <div v-if="can('patients.get')" class="link" @click="$root.goTo(previewItemUrl(item))">
                    {{ item.name }}
                </div>
                <div v-else>
                    {{ item.name }}
                </div>
            </template>

            <template #[`item.health_status`]="{ item }">
                <div v-if="healthStatus(item)" class="grid-status">
                    <span>
                        {{ healthStatus(item) }}
                    </span>
                </div>
            </template>

            <template #[`item.prohibited`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.prohibited">
                        {{ t('patients::prohibited.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::prohibited.false') }}
                    </span>
                </div>
            </template>

            <template #[`item.max_dispensing`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            item.max_dispensing ? t('patients::max_dispensing.yes') : t('patients::max_dispensing.no')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.has_treatment_break`]="{ item }">
                <div class="grid-status">
                    <span
                        v-text="
                            item.has_treatment_break
                                ? t('patients::treatment_break.true')
                                : t('patients::treatment_break.false')
                        "
                    ></span>
                </div>
            </template>

            <template #[`item.status`]="{ item }">
                {{ t('patients::status.' + item.status) }}
            </template>

            <template #[`item.gender`]="{ item }">
                <span class="text-capitalize">{{ item.gender }}</span>
            </template>
            <template #[`item.race`]="{ item }">
                <span class="text-capitalize">{{ item.race }}</span>
            </template>
            <template #[`item.diagnosis.diagnosis`]="{ item }">
                <span v-if="item.diagnosis.diagnosis">
                    {{ item.diagnosis.diagnosis }}
                </span>
                <span v-else>
                    {{ t('patients::previous_anti_psychotics.none') }}
                </span>
            </template>

            <template #[`item.health_information.past_history_of_neutropenia`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.health_information.past_history_of_neutropenia">
                        {{ t('patients::past_history_of_neutropenia.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::past_history_of_neutropenia.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.health_information.is_with_ben`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.health_information.is_with_ben">
                        {{ t('patients::is_with_ben.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::is_with_ben.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.schizoprenia_or_parkinson`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.schizoprenia_or_parkinson">
                        {{ t('patients::schizophrenia_or_parkinson.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::schizophrenia_or_parkinson.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.diagnosis.is_prescribed_clozapine`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.diagnosis.is_prescribed_clozapine">
                        {{ t('patients::is_prescribed_clozapine.true') }}
                    </span>
                    <span v-else>
                        {{ t('patients::is_prescribed_clozapine.false') }}
                    </span>
                </div>
            </template>
            <template #[`item.diagnosis.previous_anti_psychotics`]="{ item }">
                <div class="grid-status">
                    <span v-if="item.diagnosis.previous_anti_psychotics">
                        {{ item.diagnosis.previous_anti_psychotics }}
                    </span>
                    <span v-else>
                        {{ t('patients::previous_anti_psychotics.none') }}
                    </span>
                </div>
            </template>
            <template #[`item.diagnosis.ward_name`]="{ item }">
                <span v-if="item.diagnosis.ward_name">
                    {{ item.diagnosis.ward_name }}
                </span>
                <span v-else>
                    {{ t('patients::previous_antipsychotics.none') }}
                </span>
            </template>
            <template #[`item.health_information.cnrd_date`]="{ item }">
                <span v-if="item.health_information.cnrd_date">
                    {{ item.health_information.cnrd_date }}
                </span>
                <span v-else>
                    {{ t('patients::previous_antipsychotics.none') }}
                </span>
            </template>

            <template #[`item.actions`]="{ item }">
                <div class="table-actions">
                    <v-tooltip v-if="can('api.v1.patients.show')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(previewItemUrl(item))"
                            >
                                <v-icon>mdi-eye</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.details') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="can('patients.export-record-as-pdf')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="exportRecordAsPdf(item)"
                            >
                                <v-icon size="24">mdi-file-export-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('default::button.export_record_as_pdf') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="can('api.v1.patients.update')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <v-btn
                                color="disabled-color"
                                icon
                                v-bind="attrs"
                                v-on="on"
                                @click="$root.goTo(newBloodResult(item))"
                            >
                                <v-icon size="28">mdi-water-plus-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ t('patients::button.new_blood_result') }}</span>
                    </v-tooltip>

                    <v-tooltip v-if="can('api.v1.patients.update')" top transition="v-fab-transition">
                        <template #activator="{ on, attrs }">
                            <span v-on="on">
                                <v-btn
                                    :disabled="isHcp && item.has_change_request"
                                    color="disabled-color"
                                    icon
                                    v-bind="attrs"
                                    @click="$root.goTo(editItemUrl(item))"
                                >
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </span>
                        </template>
                        <span>
                            {{
                                isHcp && item.has_change_request
                                    ? t('patients::button.has_change_request')
                                    : t('default::button.edit')
                            }}
                        </span>
                    </v-tooltip>

                    <b-delete
                        v-if="can('api.v1.patients.destroy')"
                        :path="deleteItemUrl(item)"
                        :text="t('default::confirmation.deletion')"
                        :title="t('default::confirmation.general')"
                        icon="mdi-trash-can"
                        @deleted="triggerDeleted"
                    >
                    </b-delete>
                </div>
            </template>
            <template #[`item.last_blood_sample_color`]="{ item }">
                <div v-if="item.last_blood_sample_color" :class="`blood-status ${item.last_blood_sample_color}--text`">
                    <div :class="`status ${item.last_blood_sample_color}--text`">
                        <v-icon>icon icon-blood-result</v-icon>
                    </div>
                    <span v-text="item.last_blood_sample_color"></span>
                </div>
                <div v-else>
                    <span>{{ t('default::label.na') }}</span>
                </div>
            </template>
            <template #[`item.validity_date`]="{ item }">
                {{ item.validity_date ? moment(item.validity_date).format($root.dateFormat) : t('default::label.na') }}
            </template>

            <template #[`item.is_registered`]="{ item }">
                <div class="grid-status">
                    <span>
                        {{ item.is_registered ? t('default::label.yes') : t('default::label.no') }}
                    </span>
                </div>
            </template>
        </b-datatable>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
        initialFilters: {
            type: Object,
            required: true,
        },
        hasPharmacySite: {
            type: Boolean,
            required: false,
            default: false,
        },
        gridTitle: {
            type: String,
            required: true,
        },
        keepFiltersPrefix: {
            type: String,
            required: false,
            default: 'patients',
        },
    },
    data() {
        return {
            max_dispensing: 0,
            selectedItems: [],
            downloadingFile: false,
            datatableKey: 0,
            hiddenColumns: [
                'id',
                'has_change_request',
                'gender',
                'race',
                'diagnosis.diagnosis',
                'health_information.past_history_of_neutropenia',
                'health_information.is_with_ben',
                'schizoprenia_or_parkinson',
                'diagnosis.is_prescribed_clozapine',
                'diagnosis.previous_anti_psychotics',
                'diagnosis.ward_name',
                'health_information.cnrd_date',
                'collection_centre.name',
            ],
            ids: '',
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.blood_status'),
                    value: 'last_blood_sample_color',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.name'),
                    value: 'name',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('patients::grid.label.consultant'),
                    value: 'consultant.name',
                    width: 200,
                    sortable: true,
                },
                {
                    text: t('patients::grid.label.dms_id'),
                    value: 'dms_id',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.nhs_id'),
                    value: 'nhs_id',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.validity'),
                    value: 'validity_date',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('patients::grid.label.max_dispensing'),
                    value: 'max_dispensing',
                    sortable: true,
                    width: 160,
                },
                {
                    text: t('patients::grid.label.health_status'),
                    value: 'health_status',
                    sortable: true,
                    width: 240,
                },
                {
                    text: t('patients::grid.label.is_registered'),
                    value: 'is_registered',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.status'),
                    value: 'status',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('patients::grid.label.treatment_break'),
                    value: 'has_treatment_break',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.has_change_request'),
                    value: 'has_change_request',
                    sortable: true,
                    width: 140,
                },
                {
                    text: t('patients::grid.label.prohibited'),
                    value: 'prohibited',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.gender'),
                    value: 'gender',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('patients::grid.label.race'),
                    value: 'race',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('patients::grid.label.diagnosis'),
                    value: 'diagnosis.diagnosis',
                    sortable: true,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.past_history_of_neutropenia'),
                    value: 'health_information.past_history_of_neutropenia',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.is_with_ben'),
                    value: 'health_information.is_with_ben',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.schizoprenia_or_parkinson'),
                    value: 'schizoprenia_or_parkinson',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.is_prescribed_clozapine'),
                    value: 'diagnosis.is_prescribed_clozapine',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.previous_anti_psychotics'),
                    value: 'diagnosis.previous_anti_psychotics',
                    sortable: true,
                    width: 100,
                },
                {
                    text: t('patients::grid.label.ward_name'),
                    value: 'diagnosis.ward_name',
                    sortable: true,
                    width: 170,
                },
                {
                    text: t('patients::grid.label.cnrd_date'),
                    value: 'health_information.cnrd_date',
                    sortable: true,
                    width: 200,
                },

                {
                    text: t('patients::grid.label.collection_centre'),
                    value: 'collection_centre.name',
                    sortable: true,
                    width: 250,
                },

                {
                    text: t('default::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    width: 200,
                    align: 'end',
                },
            ],
            filtersOptions: {
                treatmentBreakOptions: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::treatment_break.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::treatment_break.false'),
                        value: 0,
                    },
                ],
                prohibited: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::prohibited.true'),
                        value: 1,
                    },
                    {
                        text: t('patients::prohibited.false'),
                        value: 0,
                    },
                ],
                status: [],
                max_dispensing: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::max_dispensing.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::max_dispensing.no'),
                        value: 0,
                    },
                ],
                validity: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('patients::validity.valid'),
                        value: 'valid',
                    },
                    {
                        text: t('patients::validity.invalid'),
                        value: 'invalid',
                    },
                ],
                health_status: [],
                is_registered: [
                    {
                        text: t('users::options.status.all'),
                        value: null,
                    },
                    {
                        text: t('consultants::options.registered.yes'),
                        value: 1,
                    },
                    {
                        text: t('consultants::options.registered.no'),
                        value: 0,
                    },
                ],
                dispensed: [
                    {
                        text: t('patients::options.dispensed.all'),
                        value: null,
                    },
                    {
                        text: t('patients::options.dispensed.yes'),
                        value: 1,
                    },
                    {
                        text: t('patients::options.dispensed.no'),
                        value: 0,
                    },
                ],
            },
        };
    },
    computed: {
        isHcp() {
            return User.group === 'hcps';
        },
    },
    created() {
        axios.get(route('api.v1.patients.config')).then((response) => {
            if (!response) {
                return;
            }
            this.filtersOptions.status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.statuses
            );

            this.filtersOptions.health_status = _.union(
                [
                    {
                        text: t('patients::status.all'),
                        value: null,
                    },
                ],
                response.data.health_statuses
            );
        });
    },
    methods: {
        async exportRecordAsPdf(item) {
            if (this.downloadingFile) {
                return;
            }

            this.downloadingFile = true;

            return await axios
                .get(route('patients.export-record-as-pdf', { patient: item.id }))
                .then((response) => {
                    const link = document.createElement('a');
                    link.href = response.request.responseURL;
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    if (!error || !error.response) {
                        return;
                    }
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(() => {
                        this.downloadingFile = false;
                    }, 2000);
                });
        },
        changeTab() {
            this.$refs.grid;
            if (!this.$refs.grid) {
                return;
            }
            this.$refs.grid.switchTab();
        },
        deleteItemUrl(item) {
            return route('api.v1.patients.destroy', { patient: item.id });
        },
        triggerDeleted() {
            this.datatableKey++;
            this.$root.notify('info', t('default::notification.deleted'));
        },
        updateSelectedItems(selectedItems) {
            this.selectedItems = selectedItems;
        },
        print_labels() {
            const ids = this.selectedItems.map((x) => x.id).join(',');
            this.$root.goTo(this.$root.route('patients.generate-labels', { ids }));
            return this.$root.notify(
                'info',
                t('patients::notification.success'),
                t('patients::notification.generate_labels')
            );
        },
        itemUrl(item) {
            return route('patients.get', { id: item.id });
        },
        editItemUrl(item) {
            return route('patients.get', { id: item.id });
        },
        previewItemUrl(item) {
            return route('patients.preview', { id: item.id });
        },
        createItemUrl(pharmacy_site_id) {
            return pharmacy_site_id
                ? this.$root.goTo(route('patients.create', { pharmacy_site_id }))
                : this.$root.goTo(route('patients.create'));
        },
        newBloodResult(item) {
            return route('patients.get', { patient: item.id, new_blood_result: true });
        },
        healthStatus(item) {
            if (item && item.health_status) {
                return _.map(item.health_status, (status) => {
                    return t('patients::health_status.' + status);
                }).join(', ');
            }
            return false;
        },
        async exportRecord(format) {
            const filters = this.$refs.grid.filters || {};
            const configHiddenColumns = this.$store.state.data_table.hiddenColumns;
            const visibleColumns = this.columns
                .filter((col) => !configHiddenColumns.includes(col.value))
                .map((col) => col.value)
                .filter(function (item) {
                    return item !== 'actions';
                });

            const link = document.createElement('a');
            link.href = route('patients.export', {
                format,
                ...filters,
                fields: visibleColumns.join(','),
            });
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    },
};
</script>

<style></style>
